import { FundingPolicyType } from '@shared/appModel';
import {
  isCareMattersAnnuity,
  isCareMattersFamily,
  isMembershipProgram,
} from '@shared/FundingPolicyTypeDef';
import { careMattersAnnuityPresentation } from './careMattersAnnuityPresentation';
import { cmTwoPresentation } from './cmTwoPresentation';
import { forecarePresentation } from './forecarePresentation';
import { genericHybridPresentation } from './genericHybridPresentation';
import { hybridLifeIraPresentation } from './hybridLifeIraPresentation';
import { indexedAnnuityCarePresentation } from './indexedAnnuityCarePresentation';
import { lifeWithRiderPresentation } from './lifeWithRiderPresentation';
import { lincolnMoneyGuardPresentation } from './lincolnMoneyGuardPresentation';
import { manhattanLifeStcPresentation } from './manhattanLifeStcPresentation';
import { membershipProgramPresentation } from './membershipProgramPresentation';
import { nylAssetFlexPresentation } from './nylAssetFlexPresentation';
import { oaAssetCarePresentation } from './oaAssetCarePresentation';
import { secureCarePresentation } from './secureCarePresentation';
import { shortTermCarePresentation } from './shortTermCarePresentation';
import { traditionalLtcPresentation } from './traditionalLtcPresentation';
import { defaultPresentation, StrategyPresentation } from './types';

export function getPresentation(
  policyType: FundingPolicyType | null | undefined,
): StrategyPresentation {
  if (!policyType) return defaultPresentation;
  if (isCareMattersFamily(policyType)) return cmTwoPresentation;
  // Nationwide CareMatters Annuity — shares the CareMatters name but not the
  // chassis, so it must be checked separately from isCareMattersFamily. The
  // death-benefit column holds the CONTRACT VALUE (not a life face), and the
  // monthly cap is derived from that value rather than fixed at issue, so the
  // generic annuity-hybrid columns would explain the right numbers with the
  // wrong math — see careMattersAnnuityStrategy.
  if (isCareMattersAnnuity(policyType)) return careMattersAnnuityPresentation;
  if (policyType === FundingPolicyType.nylAssetFlex) return nylAssetFlexPresentation;
  if (policyType === FundingPolicyType.moneyGuardFixedAdvantage) {
    return lincolnMoneyGuardPresentation;
  }
  if (
    policyType === FundingPolicyType.assetCare ||
    policyType === FundingPolicyType.jointAssetCare
  ) {
    return oaAssetCarePresentation;
  }
  // OneAmerica Indexed Annuity Care has its own annuity schedule mechanics
  // (base LTC Benefit Balance + COB rider), so it does not use the generic
  // Hybrid Life IRA presentation.
  if (policyType === FundingPolicyType.indexedAnnuityCare) {
    return indexedAnnuityCarePresentation;
  }
  // Hybrid Life IRA — OneAmerica Asset Care III plus the generic hybridLifeIra
  // type. Two-pool reimbursement (Face/AOB + COB) with a
  // withdrawal-rate-driven base cap and flat Face death benefit — see
  // hybridLifeIraStrategy. Modeling is unverified (no carrier illustration).
  // Joint variants are not yet wired (they fall through to legacy).
  if (
    policyType === FundingPolicyType.hybridLifeIra ||
    policyType === FundingPolicyType.assetcareThree
  ) {
    return hybridLifeIraPresentation;
  }
  // Global Atlantic ForeCare and the generic annuityHybrid type share the
  // single-pool annuity-hybrid presentation (single pool + residual Contract
  // Value floor) — see forecareStrategy.
  if (
    policyType === FundingPolicyType.forecareFixedAnnuity ||
    policyType === FundingPolicyType.annuityHybrid
  ) {
    return forecarePresentation;
  }
  // Securian SecureCare III/IV — same two-pool + indemnity table shape as the
  // generic hybrid, so it reuses genericHybridPresentation's logic but overrides
  // the death-benefit tooltip: SecureCare depletes the face PROPORTIONALLY (not
  // dollar-for-dollar). See secureCarePresentation / secureCareStrategy.
  if (
    policyType === FundingPolicyType.secureCareThree ||
    policyType === FundingPolicyType.secureCareFour
  ) {
    return secureCarePresentation;
  }
  // Generic two-pool hybrid life insurance (death-benefit pool + COB) — Allstate
  // Whole Life, Trustmark Life + Care (agisGroupPolicy), the generic
  // hybridLifeInsurance type, and its joint counterpart (jointHybridAssetBased —
  // shared pools across both spouses). Static death benefit, depleted
  // dollar-for-dollar by base drawdown — see genericHybridStrategy.
  if (
    policyType === FundingPolicyType.allstateHybridPolicy ||
    policyType === FundingPolicyType.agisGroupPolicy ||
    policyType === FundingPolicyType.hybridLifeInsurance ||
    policyType === FundingPolicyType.jointHybridAssetBased
  ) {
    return genericHybridPresentation;
  }
  // Life insurance with an LTC acceleration rider — single accelerable pool +
  // residual death benefit. Serves the generic lifeInsuranceWithRider,
  // Transamerica FFIUL (lifeWithIndexAccount), and Nationwide No-Lapse
  // Guarantee UL II (noLapseGuaranteeLife) — see lifeWithRiderStrategy.
  if (
    policyType === FundingPolicyType.lifeInsuranceWithRider ||
    policyType === FundingPolicyType.lifeWithIndexAccount ||
    policyType === FundingPolicyType.noLapseGuaranteeLife
  ) {
    return lifeWithRiderPresentation;
  }
  // CCAH membership programs (Fortified Life, Stay Your Way) are single-pool,
  // no death benefit — a thin wrapper over the traditional-LTC presentation
  // that drops the waiting-period / 2-ADL gate copy (the product has neither)
  // and renders "Uncapped" when no daily benefit is configured. Routed before
  // the bare longTermCareInsurance check since membership types are distinct
  // enum values.
  if (isMembershipProgram(policyType)) return membershipProgramPresentation;
  // Short-Term Care Insurance is trad-LTC plus its DEFINING lifetime
  // benefit-period day cap — a thin wrapper over the traditional presentation
  // that adds the day cap to the payout formula and the "benefit period
  // exhausted · Nd" OOP line — see shortTermCareStrategy.
  if (policyType === FundingPolicyType.shortTermCareInsurance) {
    return shortTermCarePresentation;
  }
  // ManhattanLife OmniFlex STC — day-denominated (no dollar pool, no death
  // benefit): the pool cell surfaces the benefit-period / lifetime DAY
  // counters and Restoration, the benefit cap follows the Accrued Daily
  // Benefit curve, and the paid cell annotates Fast-50 payments — see
  // manhattanLifeStcStrategy.
  if (policyType === FundingPolicyType.manhattanLifeOmniFlexStc) {
    return manhattanLifeStcPresentation;
  }
  // Traditional / standalone LTC (generic) and CareScout Care Assurance share
  // the single-pool reimbursement presentation. The standalone-LTC carrier
  // siblings (NYL SecureCare, Thrivent LTC, NGL EssentialLTC, Mutual of Omaha
  // MutualCare) share the same generic standalone-LTC calc path. The joint
  // ShareCare variants (nylJointSecureCare / jointNglEssentialLtc /
  // jointThriventLongTermCareInsurance / jointMutualOfOmahaMutualcare — the full
  // ShareCare set in traditionalLtcStrategy.policyTypeHasSharedPool) also route
  // to the joint-aware traditionalLtcStrategy, so they reuse this presentation
  // too. Generic Joint Traditional LTC (jointLongTermCareInsurance) routes to the
  // same strategy as a plain single-pool joint policy (no ShareCare 1/3 split),
  // so it reuses this presentation as well.
  if (
    policyType === FundingPolicyType.longTermCareInsurance ||
    policyType === FundingPolicyType.careScoutCareAssurance ||
    policyType === FundingPolicyType.nylSecureCare ||
    policyType === FundingPolicyType.thriventLongTermCareInsurance ||
    policyType === FundingPolicyType.nglEssentialLtc ||
    policyType === FundingPolicyType.mutualOfOmahaMutualcare ||
    policyType === FundingPolicyType.nylJointSecureCare ||
    policyType === FundingPolicyType.jointNglEssentialLtc ||
    policyType === FundingPolicyType.jointThriventLongTermCareInsurance ||
    policyType === FundingPolicyType.jointMutualOfOmahaMutualcare ||
    policyType === FundingPolicyType.jointLongTermCareInsurance
  ) {
    return traditionalLtcPresentation;
  }
  return defaultPresentation;
}
